import Head from "next/head";
import Link from "next/link";
import { RichText } from "prismic-reactjs";
import styled from "styled-components";
import BigCopy from "~/components/BigCopy";
import Block, { BlockContent, BlockTopSpacer, BlockWrapper } from "~/components/Block";
import BreadCrumbs from "~/components/BreadCrumbs";
import ButtonArrow from "~/components/ButtonArrow";
import Divider from "~/components/Divider";
import LinkButton from "~/components/LinkButton";
import { SimpleQuestionList } from "~/components/QuestionList";
import { linkResolver } from "~/external/prismic";
import { prismic } from "~/external/v2/prismic";
import {
  CommonPageProps,
  QuestionCategoryDocument,
  QuestionDocument,
} from "~/external/v2/types";
import theme, { smallerThan } from "~/styles/theme";
import Error from "~/views/Error";

const QuestionContentOuter = styled(BlockContent)<any>``;

const QuestionContentBody = styled(BlockContent)<any>`
  // min-height: 20vh;
  padding: ${(p) => p.theme.sizes.vSpacing}rem 50% ${(p) => p.theme.sizes.vSpacing}rem 0;
  line-height: 2;

  ${(p) => smallerThan(p.theme.breakpoints.smallDesktop)} {
    padding: ${(p) => p.theme.sizes.vSpacing}rem 35% ${(p) => p.theme.sizes.vSpacing}rem
      0;
  }

  ${(p) => smallerThan(p.theme.breakpoints.ipadLandscape)} {
    padding: ${(p) => p.theme.sizes.vSpacingSmall}rem 25%
      ${(p) => p.theme.sizes.vSpacingSmall}rem 0;
  }
  ${(p) => smallerThan(p.theme.breakpoints.small)} {
    padding: ${(p) => p.theme.sizes.vSpacingSmall}rem 0;
  }
`;

const QuestionContentRelated = styled(BlockContent)<any>`
  /*padding: 0 2%;
  margin: 2% 4%;*/

  padding-top: ${(p) => p.theme.sizes.vSpacing * 10}rem;

  ${BigCopy} h5, ${BigCopy} h6 {
    margin-bottom: 1%;
    margin-top: 0;
  }

  /*${(p) => smallerThan(p.theme.breakpoints.small)} {
    padding: 0;
    margin: 0;
  }*/

  ${BlockWrapper} > div {
    padding: 0;
    & > ${BlockContent} {
      padding: 0 0.2rem;
    }
  }
`;

const QuestionContentEnd = styled(BlockContent)<any>`
  min-height: 40vh;
  padding: 0 4%;
  margin: 4%;
  background-color: ${theme.colors.greyLighter};
  h3 {
    text-align: center;
  }

  ${(p) => smallerThan(p.theme.breakpoints.small)} {
    padding: 0;
    margin: 0;
    h3 {
      font-size: smaller;
      /*min-height: 30vh;*/
    }
  }
  div:last-child {
    text-align: center;
    margin-top: 2em;

    a {
      color: ${(p) => p.theme.colors.dark};
      text-decoration: none;
      &:hover {
        text-decoration: underline;
      }
    }
  }
`;

interface QuestionCategoryPageProps extends CommonPageProps {
  questionCategory: QuestionCategoryDocument;
  questions: QuestionDocument[];
}

function QuestionCategoryPage(props: QuestionCategoryPageProps) {
  if (props.error) return <Error {...props.error} />;

  const { questionCategory, questions } = props;

  const questionTypeSplit = questionCategory.content.type.split("/");
  const questionType =
    questionTypeSplit.length == 2
      ? {
          singular: questionTypeSplit[0],
          plural: questionTypeSplit[1],
        }
      : { singular: "Question", plural: "Questionss" };

  const breadcrumbs = [["Help", "/help"]];
  if (questionCategory) {
    breadcrumbs.push([questionCategory.content.title]);
  }

  return (
    <>
      <Head>
        <title>{questionCategory.content.title} | Help | AIAIAI</title>
        <meta
          name="description"
          content={`All ${questionType.plural.toLowerCase()}.`}
          key="meta-d"
        />
        {/* <meta key="meta-r" name="robots" content="index, follow" /> */}
      </Head>
      <Block>
        <BlockTopSpacer />
        <QuestionContentOuter>
          <BreadCrumbs linkAll={false} breadcrumbs={breadcrumbs} />
          <Divider opacity={0.26} />
          <BigCopy fontWeight={800}>
            <h3 style={{ margin: 0 }}>{questionCategory.content.title}</h3>
          </BigCopy>
          {/* <p
            style={{
              textAlign: 'center',
              fontWeight: 'bold',
              margin: '-1rem 0 1rem 0'
            }}
          >
            {document.content.category}
          </p>
          <HorizontalRule /> */}
        </QuestionContentOuter>
        <Block>
          <QuestionContentBody>
            <RichText
              render={questionCategory.content.description}
              linkResolver={linkResolver}
            />
          </QuestionContentBody>
        </Block>
      </Block>

      {/*
      <Block>
        <BlockContent>
          <BigCopy fontWeight={800}>
            <h2 style={{ fontSize: '0.83em', marginTop: 0 }}>
              Popular questions
            </h2>
          </BigCopy>
        </BlockContent>
        <BlockContent>
          <SimpleQuestionList>
            {questionCategory.content.questions.map((item, i) => (
              <li key={i}>
                <Link
                  href={`/help/${questionCategory.uid}/${item.question.uid}`}
                  passHref={true}
                >
                  <a>{item.question.data.title}</a>
                </Link>
              </li>
            ))}
          </SimpleQuestionList>
        </BlockContent>
        <BlockTopSpacer spacing="topBar" />
      </Block>
      */}

      <Block>
        <BlockContent>
          <BigCopy fontWeight={800}>
            <h3 style={{ fontSize: "0.8em", marginBottom: 0 }}>
              All {questionType.plural.toLowerCase()}
            </h3>
          </BigCopy>
        </BlockContent>
        <BlockContent>
          <SimpleQuestionList>
            {questions.map((question, i) => (
              <li key={i}>
                <Link href={`/help/${questionCategory.uid}/${question.uid}`} passHref>
                  <a>{question.content.title}</a>
                </Link>
              </li>
            ))}
          </SimpleQuestionList>
        </BlockContent>
        <BlockTopSpacer spacing="topBar" />
      </Block>

      <Block>
        <BlockContent>
          <BigCopy fontWeight={800}>
            <h3>Can’t find what you’re looking for?</h3>
          </BigCopy>
        </BlockContent>
        <BlockContent>
          <LinkButton
            buttonComponent={ButtonArrow}
            buttonText="Contact us"
            href="/help/contact"
          />
        </BlockContent>
        <BlockTopSpacer spacing="topBar" />
      </Block>
    </>
  );
}

export const getServerSideProps = async ({ query, res }) => {
  const document = await prismic.questionCategory.get.single(query.questionCategoryUid);

  // console.log('document =>', JSON.stringify(document, null, 2));

  /* const results = await prismic.question.fullText('tma-2');

  console.log('results =>', JSON.stringify(results, null, 2)); */

  if (!document) {
    res.statusCode = 404;
    // context.res.end();
    return {
      props: {
        error: {
          statusCode: 404,
          message: "Document not found",
        },
      },
    };
  } /*

  const similar = await prismic.question.similar(document.id, 30);
  document.content.related_questions = document.content.related_questions || [];

  for (let result of similar) {
    if (document.content.related_questions.length == 5) break;
    if (
      !document.content.related_questions.some(
        item => item.related_question.id == result.id
      )
    ) {
      document.content.related_questions.push({
        // @ts-ignore:next
        related_question: {
          id: result.id,
          uid: result.uid,
          tags: result.tags,
          type: result.type,
          data: { question: result.content.question }
        }
      });
    }
  } */

  const documents = await prismic.question.query.byCategories([document.uid]);

  return {
    props: {
      questionCategory: document,
      questions: documents,
    },
  };
};

export default QuestionCategoryPage;
